/* ============================================================================
   BbLAB media enhancement — media only, never year tabs.

   Scope:
   - Story timeline (`[data-story-media]`): real <video> rendering controls
     (play / pause / mute / native progress), a photo carousel with real
     total-slide dots, and an explicit retry state on load failure.
   - Home hero (`[data-hero-media]`): silent desktop/mobile background video,
     automatically looping without native player controls, with a poster
     fallback. The 2026-10-08 PSD adds exactly three corner buttons —
     fullscreen / mute / play-pause — bound to the visible branch only, plus a
     read-only bottom progressbar (`role="progressbar"`, no slider) whose value
     comes solely from the visible film's `currentTime` / `duration`.

   It deliberately does NOT own tab switching: `bblab-content.js` keeps the
   year tabs (click + Arrow/Home/End) and toggles `hidden`/`aria-hidden`/`inert`
   on the panels, then announces each selection with a bubbling
   `bblab:story-panel-select` event on the selected panel. This script listens
   for that event and restarts only the selected, actually active film from
   zero, muted and looped; every other film is paused immediately. It also
   watches `hidden`/`aria-hidden`/`inert` mutations so a hidden or outgoing
   panel can never keep playing, and it syncs the active film once on init so
   the initially selected film autoplays even though the tab script ran first.
   Motion preferences are respected: with prefers-reduced-motion the hero video
   never autoplays and an already-playing video is paused.

   No duration is invented anywhere: the elapsed readout and progress bar are
   driven by the element's own `duration` / `currentTime` and stay empty / 0
   until the browser reports real metadata.
   ============================================================================ */
(function () {
  'use strict';

  function mediaRoots(scope, selector) {
    var root = scope || document;
    if (root.nodeType === 1 && root.matches && root.matches(selector)) return [root];
    return root.querySelectorAll ? Array.prototype.slice.call(root.querySelectorAll(selector)) : [];
  }

  function formatTime(seconds) {
    if (!isFinite(seconds) || seconds < 0) return '';
    var total = Math.floor(seconds);
    var minutes = Math.floor(total / 60);
    var secs = total % 60;
    return minutes + ':' + (secs < 10 ? '0' : '') + secs;
  }

  /* ── Story timeline video ─────────────────────────────────────────────── */
  function wireTimelineVideo(video, panel) {
    var host = video.closest('[data-media-video-host]');
    if (!host || host.__bblabTimelineBound) return;
    host.__bblabTimelineBound = true;

    var toggle = host.querySelector('[data-media-playpause]');
    var mute = host.querySelector('[data-media-mute]');
    var fullscreen = host.querySelector('[data-media-fullscreen]');
    var fullscreenError = host.querySelector('[data-media-fullscreen-error]');
    var fill = host.querySelector('[data-media-progress-fill]');
    var progress = host.querySelector('.story-timeline-progress');
    var elapsed = host.querySelector('[data-media-timecode]');
    var error = host.querySelector('[data-media-error]');
    var retry = host.querySelector('[data-media-retry]');
    var loaded = video.readyState >= 1;

    // Autoplay is always muted and looped, for the hosted film and the bundled
    // asset fallback alike. Native autoplay stays off so a film that renders in
    // a hidden/inactive panel can never start itself: playback is only ever
    // selection-triggered (see watchTimeline).
    video.muted = true;
    video.defaultMuted = true;
    video.loop = true;
    video.autoplay = false;
    if (video.removeAttribute) video.removeAttribute('autoplay');
    video.setAttribute('playsinline', '');
    // Native controls never render once the custom player is bound: the desktop
    // uses the custom buttons and the <=760 layer hides them. Drop the markup
    // attribute/property before any selection-triggered autoplay; a no-JS
    // desktop keeps the Liquid `controls` fallback because this runs only on
    // bind.
    video.controls = false;
    if (video.removeAttribute) video.removeAttribute('controls');

    function refreshPlay() {
      if (!toggle) return;
      var playing = !video.paused && !video.ended;
      toggle.setAttribute('aria-pressed', playing ? 'true' : 'false');
    }
    function refreshMute() {
      if (!mute) return;
      mute.setAttribute('aria-pressed', video.muted ? 'true' : 'false');
    }
    function refreshProgress() {
      var ratio = isFinite(video.duration) && video.duration > 0
        ? Math.min(Math.max(video.currentTime / video.duration, 0), 1)
        : 0;
      if (fill) fill.style.width = (ratio * 100) + '%';
      if (progress) {
        progress.setAttribute('aria-valuenow', String(Math.round(ratio * 100)));
        progress.setAttribute('aria-disabled', String(!loaded));
        progress.setAttribute('aria-valuetext', formatTime(video.currentTime) + ' / ' + formatTime(video.duration));
      }
      if (elapsed) {
        var current = formatTime(video.currentTime);
        elapsed.textContent = current ? current + ' / ' + formatTime(video.duration) : '';
      }
    }

    video.addEventListener('play', refreshPlay);
    video.addEventListener('pause', refreshPlay);
    video.addEventListener('ended', refreshPlay);
    video.addEventListener('timeupdate', refreshProgress);
    video.addEventListener('durationchange', refreshProgress);
    video.addEventListener('loadedmetadata', function () {
      loaded = true;
      if (error) error.hidden = true;
      refreshProgress();
    });
    video.addEventListener('volumechange', refreshMute);
    video.addEventListener('error', function () {
      if (error) error.hidden = false;
      loaded = false;
      refreshProgress();
    });

    if (toggle) {
      toggle.addEventListener('click', function () {
        if (video.paused || video.ended) {
          // Never start playback in a hidden panel; the visitor must see it.
          if (panel && panel.hidden) return;
          if (video.ended || (isFinite(video.duration) && video.currentTime >= video.duration)) video.currentTime = 0;
          var started = video.play();
          if (started && started.catch) started.catch(function () { refreshPlay(); });
        } else {
          video.pause();
        }
      });
    }
    if (mute) {
      mute.addEventListener('click', function () {
        video.muted = !video.muted;
        refreshMute();
      });
    }
    if (retry) {
      retry.addEventListener('click', function () {
        if (error) error.hidden = true;
        video.load();
      });
    }
    if (fullscreen) {
      fullscreen.hidden = !host.requestFullscreen || !document.fullscreenEnabled;
      function refreshFullscreen() {
        var active = document.fullscreenElement === host;
        fullscreen.setAttribute('aria-pressed', String(active));
        fullscreen.setAttribute('aria-label', active ? '全画面表示を終了' : '全画面表示');
      }
      fullscreen.addEventListener('click', function () {
        if (panel && panel.hidden) return;
        if (fullscreenError) fullscreenError.hidden = true;
        var attempt = document.fullscreenElement === host ? document.exitFullscreen() : host.requestFullscreen();
        if (attempt && attempt.catch) attempt.catch(function () {
          refreshFullscreen();
          if (fullscreenError) fullscreenError.hidden = false;
        });
      });
      document.addEventListener('fullscreenchange', refreshFullscreen);
      refreshFullscreen();
    }
    if (progress) {
      function seek(seconds) {
        if (!loaded || !isFinite(video.duration) || video.duration <= 0) return;
        video.currentTime = Math.min(Math.max(seconds, 0), video.duration);
        refreshProgress();
      }
      progress.addEventListener('click', function (event) {
        var rect = progress.getBoundingClientRect();
        if (!rect.width) return;
        seek(((event.clientX - rect.left) / rect.width) * video.duration);
      });
      progress.addEventListener('keydown', function (event) {
        var seconds;
        if (event.key === 'ArrowRight' || event.key === 'ArrowUp') seconds = video.currentTime + 5;
        else if (event.key === 'ArrowLeft' || event.key === 'ArrowDown') seconds = video.currentTime - 5;
        else if (event.key === 'Home') seconds = 0;
        else if (event.key === 'End') seconds = video.duration;
        else return;
        event.preventDefault();
        seek(seconds);
      });
    }

    refreshPlay();
    refreshMute();
    refreshProgress();
    // The selection spotlight lives on the timeline, so expose the real
    // play-state repaint for the rejected-autoplay path (truthful button state).
    video.__bblabRefreshPlay = refreshPlay;
  }

  /* ── Story timeline photo carousel ────────────────────────────────────── */
  function wireCarousel(carousel) {
    if (carousel.__bblabCarouselBound) return;
    carousel.__bblabCarouselBound = true;
    var track = carousel.querySelector('[data-media-track]');
    if (!track) return;
    var slides = Array.prototype.slice.call(track.children);
    if (slides.length < 2) return;

    // The control row is a sibling of the media box (the batch-2 markup moved it
    // out of the carousel), so read it from the owning panel instead of the
    // carousel. Looking it up on the carousel itself silently bound nothing.
    var panel = carousel.closest ? carousel.closest('[data-story-panel]') : null;
    var controlHost = panel || carousel.parentElement || carousel;
    var prev = controlHost.querySelector('[data-media-prev]');
    var next = controlHost.querySelector('[data-media-next]');
    var dots = controlHost.querySelector('[data-media-dots]');
    var counter = controlHost.querySelector('[data-media-counter]');
    var peek = !!(carousel.classList && carousel.classList.contains('story-media-carousel--peek'));
    var initial = parseInt(carousel.getAttribute('data-media-initial-index'), 10);
    var current = isFinite(initial) ? Math.min(Math.max(initial, 0), slides.length - 1) : 0;
    var dotsList = [];

    // Peek is centred from the real layout box: the slide's own offset (which
    // already folds in the track padding and gap) plus half its width against
    // half the viewport. This stays correct on the <=1200px single-card layout
    // without re-deriving the 44.7vw/1.2vw literals in JS.
    function peekOffset() {
      var slide = slides[current];
      if (!slide) return null;
      var viewport = carousel.clientWidth || track.clientWidth || 0;
      var slideWidth = slide.offsetWidth || 0;
      if (!viewport || !slideWidth) return null;
      var center = (slide.offsetLeft || 0) + slideWidth / 2;
      return viewport / 2 - center;
    }

    function paint() {
      if (peek) {
        var offset = peekOffset();
        // A hidden panel has a zero box: leave the transform untouched until the
        // visibility observer can measure the real layout.
        if (offset !== null) track.style.transform = 'translateX(' + offset + 'px)';
      } else {
        track.style.transform = 'translateX(-' + (current * 100) + '%)';
      }
      slides.forEach(function (slide, i) {
        slide.setAttribute('aria-hidden', i === current ? 'false' : 'true');
        slide.toggleAttribute('inert', i !== current);
      });
      dotsList.forEach(function (dot, i) {
        dot.setAttribute('aria-current', i === current ? 'true' : 'false');
      });
      // Real media count only: a single film never renders a "3 / 3".
      if (counter) counter.textContent = (current + 1) + ' / ' + slides.length;
    }

    function go(index) {
      current = (index + slides.length) % slides.length;
      paint();
    }

    function repaint() { paint(); }

    // Re-measure on resize and when the 1200px peek breakpoint flips. Bound
    // once per carousel (the __bblabCarouselBound guard above also protects the
    // theme-editor re-init from adding duplicate listeners).
    if (window.addEventListener) window.addEventListener('resize', repaint, { passive: true });
    if (window.matchMedia) {
      var peekBreakpoint = window.matchMedia('(max-width: 1200px)');
      if (peekBreakpoint.addEventListener) peekBreakpoint.addEventListener('change', repaint);
      else if (peekBreakpoint.addListener) peekBreakpoint.addListener(repaint);
    }
    // The 2024 peek panel starts hidden, so its first paint has no box to
    // measure. Re-centre as soon as the tab script reveals it.
    if (typeof MutationObserver === 'function' && panel) {
      var visibility = new MutationObserver(repaint);
      visibility.observe(panel, { attributes: true, attributeFilter: ['hidden'] });
      carousel.__bblabCarouselObserver = visibility;
    }

    if (dots) {
      slides.forEach(function (slide, i) {
        var dot = document.createElement('button');
        dot.type = 'button';
        dot.setAttribute('aria-label', String(i + 1));
        dot.addEventListener('click', function () { go(i); });
        dot.addEventListener('keydown', function (event) {
          if (event.key === 'ArrowRight') { event.preventDefault(); go(i + 1); dotsList[(i + 1) % dotsList.length].focus(); }
          else if (event.key === 'ArrowLeft') { event.preventDefault(); go(i - 1); dotsList[(i - 1 + dotsList.length) % dotsList.length].focus(); }
        });
        dots.appendChild(dot);
        dotsList.push(dot);
      });
      dots.hidden = false;
    }
    if (prev) prev.addEventListener('click', function () { go(current - 1); });
    if (next) next.addEventListener('click', function () { go(current + 1); });
    paint();
  }

  /* ── Story timeline: selection-driven autoplay, hidden-film pause ────────
     The tab controller owns selection and announces every one of its paths
     (click, Arrow/Home/End, re-selecting the active tab, the initial select)
     with a bubbling `bblab:story-panel-select` event. This layer only reacts:
     it restarts the selected active film from zero, muted and looped, pauses
     every other panel's film immediately, and never plays a panel that is
     hidden or marked off with the controller's aria-hidden/inert pair. ------ */
  var STORY_SELECT_EVENT = 'bblab:story-panel-select';

  function isStoryPanel(node) {
    return !!(node && typeof node.hasAttribute === 'function' && node.hasAttribute('data-story-panel'));
  }

  // A panel counts as active only when it is not hidden AND not marked off by
  // aria-hidden="true" or inert: the animated outgoing panel keeps hidden=false
  // while it slides out, so `hidden` alone would wrongly call it active.
  function panelActive(panel) {
    if (!panel) return false;
    if (panel.hidden) return false;
    if (typeof panel.getAttribute === 'function' && panel.getAttribute('aria-hidden') === 'true') return false;
    if (typeof panel.hasAttribute === 'function' && panel.hasAttribute('inert')) return false;
    return true;
  }

  function panelVideos(panel) {
    if (!panel || typeof panel.querySelectorAll !== 'function') return [];
    return Array.prototype.slice.call(panel.querySelectorAll('video'));
  }

  function pausePanelVideos(panel) {
    panelVideos(panel).forEach(function (video) {
      if (!video.paused) video.pause();
    });
  }

  function refreshFilmButton(video) {
    if (video && typeof video.__bblabRefreshPlay === 'function') video.__bblabRefreshPlay();
  }

  // Muted autoplay is what the browser policy allows, so every visit is muted.
  function startFilm(video) {
    if (!video) return;
    video.muted = true;
    video.defaultMuted = true;
    video.loop = true;
    if (video.setAttribute) video.setAttribute('muted', '');
    var attempt = null;
    try { attempt = video.play(); } catch (error) { attempt = null; }
    // A rejected play() must not leave a lying play/pause button.
    if (attempt && attempt.catch) attempt.catch(function () { refreshFilmButton(video); });
  }

  function restartFilm(video) {
    if (!video) return;
    try { video.currentTime = 0; } catch (error) { /* pre-metadata seeks are safe to ignore */ }
    startFilm(video);
  }

  function firstActivePanel(panels) {
    for (var i = 0; i < panels.length; i += 1) {
      if (panelActive(panels[i])) return panels[i];
    }
    return null;
  }

  function syncTimelineSelection(timeline, selected, restart) {
    var panels = Array.prototype.slice.call(timeline.querySelectorAll('[data-story-panel]'));
    // A selection for a hidden/aria-hidden/inert panel is stale (rapid switch,
    // outgoing animation): fall back to whichever panel is really active.
    var chosen = panelActive(selected) ? selected : firstActivePanel(panels);
    panels.forEach(function (panel) {
      if (panel !== chosen) pausePanelVideos(panel);
    });
    if (!chosen) return;
    panelVideos(chosen).forEach(function (video) {
      if (!restart && !video.paused && !video.ended) return; // already running
      // A real selection restarts from zero; the initial paint only starts the
      // fresh film (which is already at zero) without seeking it back.
      if (restart) restartFilm(video);
      else startFilm(video);
    });
  }

  function watchTimeline(timeline) {
    if (!timeline || timeline.__bblabTimelineWired) return;
    var panels = Array.prototype.slice.call(timeline.querySelectorAll('[data-story-panel]'));
    if (!panels.length) return;
    timeline.__bblabTimelineWired = true;

    // The initial select() already ran in the tab script (which loads first),
    // so its event was missed: picking the active film here is what autoplays
    // the selected film on first paint without starting any hidden one.
    syncTimelineSelection(timeline, firstActivePanel(panels), false);

    timeline.addEventListener(STORY_SELECT_EVENT, function (event) {
      var selected = event && event.detail ? event.detail.panel : null;
      if (!isStoryPanel(selected) && isStoryPanel(event && event.target)) selected = event.target;
      syncTimelineSelection(timeline, selected, true);
    });

    // Guard the controller's visibility mutations: pause a film as soon as its
    // panel is hidden or marked aria-hidden/inert (the outgoing slide).
    if (typeof MutationObserver === 'function') {
      var observer = new MutationObserver(function (records) {
        records.forEach(function (record) {
          var target = record.target;
          if (!isStoryPanel(target)) return;
          if (!panelActive(target)) pausePanelVideos(target);
        });
      });
      panels.forEach(function (panel) {
        observer.observe(panel, { attributes: true, attributeFilter: ['hidden', 'aria-hidden', 'inert'] });
      });
      // Keep the reference so the observer is not collected while the section lives.
      timeline.__bblabMediaObserver = observer;
    }
  }

  function initTimeline(scope) {
    var timelines = [];
    mediaRoots(scope, '[data-story-media]').forEach(function (panel) {
      var video = panel.querySelector('[data-media-video-host] video');
      if (video) wireTimelineVideo(video, panel);
      mediaRoots(panel, '[data-media-carousel]').forEach(wireCarousel);
      var timeline = panel.closest ? panel.closest('[data-bblab-story-timeline]') : null;
      // Wire every film in the section before the selection spotlight runs, so
      // the initial play finds fully bound controls.
      if (timeline && timelines.indexOf(timeline) === -1) timelines.push(timeline);
    });
    timelines.forEach(watchTimeline);
  }

  /* ── Home hero video ──────────────────────────────────────────────────── */

  // Both the desktop and the mobile `<video>` can be in the DOM at once (and
  // `video_tag: autoplay` marks them both). Only the branch CSS actually shows
  // may play, so everything hidden by the 760px breakpoint is paused.
  function heroVideoVisible(video) {
    if (!video || video.hidden) return false;
    var style = window.getComputedStyle ? window.getComputedStyle(video) : null;
    if (style && style.display === 'none') return false;
    return video.offsetParent !== null;
  }

  // Page lifecycle gate for the hero: a backgrounded/hidden document must never
  // keep a film running, and only a visible document may resume. `visibilityState`
  // is the modern signal; `hidden` is the older WebKit fallback.
  function documentHidden() {
    if (typeof document.visibilityState === 'string') return document.visibilityState === 'hidden';
    return !!document.hidden;
  }

  // Trusted-gesture recovery only counts for a hero host that is really on
  // screen: a zero-size box (hidden branch or display:none ancestor) or a rect
  // that never overlaps the viewport must not turn a stray tap into playback.
  function heroHostInViewport(host) {
    if (!host || typeof host.getBoundingClientRect !== 'function') return false;
    var rect = host.getBoundingClientRect();
    if (!rect) return false;
    var width = rect.width || 0;
    var height = rect.height || 0;
    if (width <= 0 || height <= 0) return false;
    var left = rect.left || 0;
    var top = rect.top || 0;
    var right = isFinite(rect.right) ? rect.right : left + width;
    var bottom = isFinite(rect.bottom) ? rect.bottom : top + height;
    var viewportWidth = window.innerWidth || (document.documentElement ? document.documentElement.clientWidth : 0) || 0;
    var viewportHeight = window.innerHeight || (document.documentElement ? document.documentElement.clientHeight : 0) || 0;
    if (viewportWidth <= 0 || viewportHeight <= 0) return false;
    return right > 0 && bottom > 0 && left < viewportWidth && top < viewportHeight;
  }

  // Only the browser's own autoplay-policy rejection earns the gesture path.
  // A generic media/decode failure keeps the poster-only fallback and must
  // never be mistaken for a recoverable policy denial.
  function policyDenied(error) {
    return !!(error && error.name === 'NotAllowedError');
  }

  function initHero(scope) {
    mediaRoots(scope, '[data-hero-media]').forEach(function (host) {
      if (host.__bblabHeroBound) return;
      host.__bblabHeroBound = true;
      var videos = Array.prototype.slice.call(host.querySelectorAll('video'));
      if (!videos.length) return;
      var motion = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
      var breakpoint = window.matchMedia ? window.matchMedia('(max-width: 760px)') : null;
      var poster = host.querySelector('picture');
      var fullscreen = host.querySelector('[data-hero-fullscreen]');
      var mute = host.querySelector('[data-hero-mute]');
      var playpause = host.querySelector('[data-hero-playpause]');
      var progress = host.querySelector('[data-hero-progress]');
      var progressFill = host.querySelector('[data-hero-progress-fill]');
      var timecode = host.querySelector('[data-hero-timecode]');
      // The three controls are the only player UI. User intent survives the
      // desktop/mobile branch swap: the same mute and play/pause state is
      // re-applied to whichever film becomes visible.
      var userMuted = true;
      var userPaused = false;

      function visibleVideo() {
        for (var i = 0; i < videos.length; i++) {
          if (heroVideoVisible(videos[i])) return videos[i];
        }
        return null;
      }

      function refreshMute() {
        if (!mute) return;
        var current = visibleVideo() || videos[0];
        var muted = current ? !!current.muted : true;
        mute.setAttribute('aria-pressed', muted ? 'true' : 'false');
        mute.setAttribute('aria-label', muted ? 'ミュートを解除' : 'ミュートにする');
      }

      function refreshPlay() {
        if (!playpause) return;
        var current = visibleVideo();
        var playing = !!(current && !current.paused && !current.ended);
        playpause.setAttribute('aria-pressed', playing ? 'true' : 'false');
        playpause.setAttribute('aria-label', playing ? '一時停止' : '再生');
      }

      // Read-only progressbar plus real elapsed label. Only the film that is
      // actually visible feeds the value from its own currentTime/duration;
      // with no real metadata the ratio stays 0 and the label stays blank
      // rather than inventing a duration. The bar is never a slider and gains
      // no pointer/keyboard seek behaviour.
      function refreshProgress() {
        var current = visibleVideo();
        var valid = !!(current && isFinite(current.duration) && current.duration > 0);
        var ratio = valid
          ? Math.min(Math.max(current.currentTime / current.duration, 0), 1)
          : 0;
        var label = valid ? formatTime(current.currentTime) + ' / ' + formatTime(current.duration) : '';
        if (progressFill) progressFill.style.width = (ratio * 100) + '%';
        if (progress) {
          progress.setAttribute('aria-valuenow', String(Math.round(ratio * 100)));
          progress.setAttribute('aria-valuetext', label);
        }
        if (timecode) {
          timecode.textContent = label;
          timecode.hidden = !label;
        }
      }

      function applyMute() {
        videos.forEach(function (video) {
          video.muted = userMuted;
          if (userMuted) video.setAttribute('muted', '');
          else video.removeAttribute('muted');
        });
        refreshMute();
      }

      // Reveal the shared static fallback only for the film that is still the
      // live visible branch and is genuinely not playing. A stale rejection
      // from a branch that was swapped out (or that has since recovered) must
      // never cover a different currently-playing film.
      function posterFallback(video) {
        if (!poster) return;
        if (video !== visibleVideo()) return;
        if (!video.paused && !video.ended) return;
        poster.hidden = false;
      }

      // Issue play() at most once per eligible film. A film that is already
      // playing is never re-played (no restart, no duplicate play event, no
      // clock reset). A synchronous throw (some embedded WebViews) and a
      // rejected promise are both treated as a real failure and expose the
      // truthful static fallback; no injected UI. Failure state belongs only to
      // the newest attempt for this film and only while it is still paused, so
      // an obsolete rejection (an attempt a later success already superseded, or
      // an older attempt while a newer one is pending) is ignored outright: it
      // can neither reinstate a denial nor re-cover the poster. The newest
      // still-paused attempt records exactly policyDenied(error) - true only for
      // NotAllowedError, so a generic failure also clears a stale denial.
      function playVideo(video) {
        if (!video.paused) return;
        var token = (video.__bblabHeroPlayToken || 0) + 1;
        video.__bblabHeroPlayToken = token;
        function fail(error) {
          if (video.__bblabHeroPlayToken !== token) return;
          if (!video.paused) return;
          video.__bblabHeroPolicyDenied = policyDenied(error);
          posterFallback(video);
        }
        var attempt;
        try {
          attempt = video.play();
        } catch (error) {
          fail(error);
          return;
        }
        if (attempt && attempt.catch) attempt.catch(fail);
      }

      function syncPlayback() {
        var reduced = !!(motion && motion.matches);
        var hidden = documentHidden();
        if (reduced && poster) poster.hidden = false;
        videos.forEach(function (video) {
          if (reduced || hidden || userPaused || !heroVideoVisible(video) || video.error) {
            if (!video.paused) video.pause();
            return;
          }
          playVideo(video);
        });
        // A native autoplay film may already be playing before these listeners
        // attach, so no `play` event follows. Hide the static fallback from the
        // live playback state instead of relying on that event.
        if (poster && !reduced && !hidden && !userPaused) {
          var current = visibleVideo();
          if (current && !current.paused && !current.ended) poster.hidden = true;
        }
        refreshPlay();
        refreshProgress();
      }

      // A trusted touch/click is the only signal WebKit accepts for a hero whose
      // autoplay the browser policy rejected. Every gate must hold; play() then
      // runs synchronously through the guarded syncPlayback inside the real
      // gesture (no timer, no promise, no preventDefault), so the gesture is
      // never consumed and a rejected retry stays recoverable by a later tap.
      function recoverFromGesture(event) {
        if (!event || event.isTrusted !== true) return;
        if (!breakpoint || !breakpoint.matches) return;
        if (documentHidden()) return;
        if (motion && motion.matches) return;
        if (userPaused) return;
        if (!heroHostInViewport(host)) return;
        var video = visibleVideo();
        if (!video || !video.__bblabHeroPolicyDenied) return;
        if (!video.paused || video.ended) return;
        if (video.error) return;
        syncPlayback();
      }

      document.addEventListener('touchend', recoverFromGesture);
      document.addEventListener('click', recoverFromGesture);

      videos.forEach(function (video) {
        video.muted = true;
        video.defaultMuted = true;
        video.loop = true;
        video.controls = false;
        video.setAttribute('muted', '');
        video.setAttribute('playsinline', '');
        video.setAttribute('webkit-playsinline', '');
        video.setAttribute('aria-hidden', 'true');
        video.setAttribute('tabindex', '-1');
        video.removeAttribute('controls');
        video.addEventListener('play', function () {
          // Native autoplay can fire in a hidden document (tab backgrounded at
          // load); a lifecycle pause must not be undone by that late play.
          if (!heroVideoVisible(video) || (motion && motion.matches) || documentHidden()) { video.pause(); return; }
          // A genuine play is the only thing that clears the recorded policy
          // denial; the recovery gate then stops offering the gesture path. It
          // also invalidates any earlier still-pending rejection, so a late
          // failure can never reinstate a stale denial over this playing film.
          video.__bblabHeroPolicyDenied = false;
          video.__bblabHeroPlayToken = (video.__bblabHeroPlayToken || 0) + 1;
          if (poster) poster.hidden = true;
          refreshPlay();
        });
        video.addEventListener('pause', refreshPlay);
        video.addEventListener('ended', refreshPlay);
        video.addEventListener('volumechange', refreshMute);
        video.addEventListener('error', function () {
          if (heroVideoVisible(video) && poster) poster.hidden = false;
        });
        // Progress follows the visible film's own clock and survives loop,
        // pause and the desktop/mobile branch swap.
        ['timeupdate', 'durationchange', 'loadedmetadata', 'seeked', 'play', 'pause', 'ended'].forEach(function (eventName) {
          video.addEventListener(eventName, refreshProgress);
        });
      });

      if (mute) {
        mute.addEventListener('click', function () {
          userMuted = !userMuted;
          applyMute();
        });
      }

      if (playpause) {
        playpause.addEventListener('click', function () {
          var current = visibleVideo();
          userPaused = !!(current && !current.paused && !current.ended);
          if (userPaused) videos.forEach(function (video) { if (!video.paused) video.pause(); });
          else syncPlayback();
          refreshPlay();
        });
      }

      if (fullscreen) {
        // A browser without the Fullscreen API keeps the slot but hides it
        // instead of leaving a dead button in the corner.
        if (!host.requestFullscreen || !document.fullscreenEnabled) fullscreen.hidden = true;
        function refreshFullscreen() {
          var active = document.fullscreenElement === host;
          fullscreen.setAttribute('aria-pressed', active ? 'true' : 'false');
          fullscreen.setAttribute('aria-label', active ? '全画面表示を終了' : '全画面表示');
        }
        fullscreen.addEventListener('click', function () {
          var attempt;
          try {
            attempt = document.fullscreenElement === host ? document.exitFullscreen() : host.requestFullscreen();
          } catch (error) {
            refreshFullscreen();
            return;
          }
          // A rejected request must never break the page: keep the background
          // film running and leave the pressed state coherent.
          if (attempt && attempt.catch) attempt.catch(function () { refreshFullscreen(); });
          else refreshFullscreen();
        });
        document.addEventListener('fullscreenchange', refreshFullscreen);
        refreshFullscreen();
      }

      [breakpoint, motion].forEach(function (query) {
        if (!query) return;
        if (query.addEventListener) query.addEventListener('change', syncPlayback);
        else if (query.addListener) query.addListener(syncPlayback);
      });

      // Media readiness can land after the first sync (slow 4.5/7.2Mbps source,
      // mobile radios). Each milestone funnels into the guarded syncPlayback, so
      // an already-playing film is never re-played and hidden / reduced-motion /
      // user-paused branches stay paused. load() is never called, so no download
      // is reset and no polling loop is introduced.
      videos.forEach(function (video) {
        ['loadedmetadata', 'loadeddata', 'canplay'].forEach(function (eventName) {
          video.addEventListener(eventName, syncPlayback);
        });
      });

      // First window load, bfcache restore and tab resume: the browser may have
      // suspended the film while hidden, so re-evaluate the CSS-visible branch.
      if (window.addEventListener) {
        window.addEventListener('load', syncPlayback);
        window.addEventListener('pageshow', syncPlayback);
      }

      // Hidden documents pause; becoming visible restores only the visible
      // branch when the user has not paused and motion is allowed.
      document.addEventListener('visibilitychange', syncPlayback);

      // If a theme stylesheet was still pending at init, the first sync may have
      // seen the wrong branch geometry. Bind each `link[rel=stylesheet]` once;
      // a pending sheet is not listed in document.styleSheets until it loads, so
      // the actual link elements are the only reliable owners. `load` only fires
      // while a sheet is still arriving, so this is a no-op on an already-styled
      // page and never loops.
      if (document.querySelectorAll) {
        Array.prototype.forEach.call(document.querySelectorAll('link[rel="stylesheet"]'), function (link) {
          if (!link || !link.addEventListener || link.__bblabHeroCssBound) return;
          link.__bblabHeroCssBound = true;
          link.addEventListener('load', syncPlayback);
        });
      }

      // Guarded viewport observer: if the browser suspended the visible branch
      // while the hero was scrolled out and no lifecycle event followed, resume
      // when the host re-enters view. It observes only this host, never polls and
      // never adds another global behaviour.
      if (typeof window.IntersectionObserver === 'function') {
        var observer = new window.IntersectionObserver(function (entries) {
          for (var i = 0; i < entries.length; i++) {
            if (entries[i].target === host && entries[i].isIntersecting) {
              syncPlayback();
              return;
            }
          }
        });
        try { observer.observe(host); } catch (error) { /* observer unsupported */ }
      }

      applyMute();
      syncPlayback();
    });
  }

  function init(scope) {
    initTimeline(scope || document);
    initHero(scope || document);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { init(document); });
  else init(document);

  // Theme editor / section reload support, mirroring the other BbLAB scripts.
  document.addEventListener('shopify:section:load', function (event) { init(event.target); });
})();
